import { formatPhoneForInput, normalizeConsultationPhone } from "@ulaw/content-types/phone";
import { isMiniApp } from './platform';
import { OpenZaloForSubmission, useZaloSubmission } from './zalo-submission';
import { useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { REGISTRATION_FORM_VERSION, REGISTRATION_TITLE, SCHOOL_PROVINCES, INTERESTED_MAJORS, SCHOOL_GRADES, REGISTRATION_ACCURACY, REGISTRATION_CONSENT, type ConsultationInput } from '@ulaw/content-types';
import { endpoint, privacyUrl } from './content';
import { ContentLink } from './links';
import { nameFailure, readZaloName } from './zalo-name';
import { phoneFailure, readZaloPhone } from './zalo-phone';
const empty: ConsultationInput = {
  fullname: '',
  school: '',
  email: '',
  phone: '',
  formVersion: REGISTRATION_FORM_VERSION,
  nationalId: '',
  schoolProvince: '',
  interestedMajors: [],
  grade: '',
  gradeOther: '',
  accuracyConfirmed: false,
  consent: false,
};
async function post(path: string, body: unknown, id?: string) {
  const response = await fetch(endpoint(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(id ? { 'Idempotency-Key': id } : {}) },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(30_000),
    cache: 'no-store',
  });
  const result = await response.json();
  if (import.meta.env.VITE_ZALO_DIAGNOSTICS === 'true')
    console.info('[hcmulaw:consultation]', {
      event: 'api-response', operation: path.endsWith('/status') ? 'status' : 'submit',
      status: response.status,
      code: typeof result.error?.code === 'string' && /^[A-Z_]{1,64}$/.test(result.error.code) ? result.error.code : null,
    });
  if (!response.ok)
    throw new Error(result.error?.message || 'Chưa thể gửi yêu cầu. Vui lòng thử lại.');
  return result;
}
export function Consultation() {
  const native = isMiniApp();
  const zalo = useZaloSubmission();
  const invitation = new URLSearchParams(window.location.search).get('invitation') || undefined;
  const [confirmZaloLink,setConfirmZaloLink] = useState(false);
  const [fields, setFields] = useState<ConsultationInput>(empty);
  const [busy, setBusy] = useState(false),
    [autofill, setAutofill] = useState(false);
  const [notice, setNotice] = useState(''),
    [error, setError] = useState('');
  const [receipt, setReceipt] = useState<string>();
  useLayoutEffect(() => {
    if (!receipt) return;
    // The receipt replaces the form on the same route; ZaUI scrolls the body.
    document.body.scrollTo?.(0, 0);
    window.scrollTo(0, 0);
  }, [receipt]);
  const [nameBusy, setNameBusy] = useState(false);
  const [namePermission, setNamePermission] = useState(false);
  const [nameNotice, setNameNotice] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneNotice, setPhoneNotice] = useState('');
  const [phonePermission, setPhonePermission] = useState(false);
  const pending = useRef<{ payload: string; id: string }>();
  const submitting = useRef(false);
  const set = (key: keyof ConsultationInput, value: string | boolean | string[]) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  async function fillName() {
    setPhoneNotice('');
    setPhonePermission(false);
    setAutofill(true);
    setNameBusy(true);
    setNamePermission(false);
    setNameNotice('');
    setNotice('');
    try {
      const name = await readZaloName();
      set('fullname', name);
      setNameNotice('Đã điền tên từ Zalo. Bạn có thể chỉnh sửa trước khi gửi.');
    } catch (cause) {
      const failure = nameFailure(cause);
      setNameNotice(failure.message);
      setNamePermission(failure.permission);
    } finally {
      setAutofill(false);
      setNameBusy(false);
    }
  }
  async function openPermissions(target: 'name' | 'phone') {
    const feedback = target === 'name' ? setNameNotice : setPhoneNotice;
    try {
      const { openPermissionSetting } = await import('zmp-sdk');
      await openPermissionSetting();
      feedback(target === 'name' ? 'Sau khi cho phép thông tin cá nhân, chọn Lấy tên Zalo để thử lại.' : 'Sau khi cho phép chia sẻ số điện thoại, chọn Lấy số Zalo để thử lại.');
    } catch {
      feedback('Chưa mở được cài đặt quyền. Trong menu Mini App, chọn Quản lý quyền. Bạn vẫn có thể nhập trực tiếp.');
    }
  }
  async function fillPhone() {
    setNameNotice('');
    setNamePermission(false);
    setPhoneNotice('');
    setPhonePermission(false);
    setPhoneBusy(true);
    setAutofill(true);
    setNotice('');
    try {
      const phone = await readZaloPhone();
      set('phone', formatPhoneForInput(phone));
      setPhoneNotice('Đã điền số điện thoại từ Zalo. Bạn có thể chỉnh sửa trước khi gửi.');
    } catch (cause) {
      const failure = phoneFailure(cause);
      setPhoneNotice(failure.message);
      setPhonePermission(failure.permission);
    } finally {
      setAutofill(false);
      setPhoneBusy(false);
    }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    if (!normalizeConsultationPhone(fields.phone)) {
      setError('Số điện thoại không hợp lệ. Vui lòng nhập số điện thoại Việt Nam với đầu số 0, 84 hoặc +84.');
      setBusy(false);
      submitting.current = false;
      return;
    }
    const payload = JSON.stringify(fields);
    if (pending.current?.payload !== payload)
      pending.current = { payload, id: crypto.randomUUID() };
    try {
      if(invitation && (!native || !confirmZaloLink)) throw new Error('Vui lòng mở Mini App Zalo và xác nhận liên kết tài khoản trước khi gửi.');
      const identity = await zalo.grant();
      const result = await post(
        '/api/consultations',
        { ...fields, ...identity, ...(invitation ? {invitation,confirmZaloLink} : {}), consentVersion: 'consultation-v5' },
        pending.current.id
      );
      if (typeof result.id !== 'string' || !['pending', 'complete', 'blocked'].includes(result.status))
        throw new Error('Chưa xác nhận được yêu cầu đã lưu. Vui lòng thử lại.');
      setReceipt(result.id);
      setFields(empty);
      pending.current = undefined;
    } catch (cause) {
      setError(
        cause instanceof Error && cause.name !== 'TimeoutError'
          ? cause.message
          : 'Chưa nhận được xác nhận. Vui lòng thử gửi lại; yêu cầu trùng sẽ không được lưu hai lần.'
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }
  if (!native) return <main className="consultation"><OpenZaloForSubmission/></main>;
  if (receipt)
    return (
      <main className="consultation">
        <section className="consultation-success" role="status">
          <h1>Đã tiếp nhận yêu cầu</h1>
          <p>Thông tin đăng ký đã được lưu để bộ phận tuyển sinh tiếp nhận.</p>
          <p>Khi đủ điều kiện nhận tin từ OA, bạn sẽ nhận lời cảm ơn và bản xác nhận toàn bộ thông tin đã gửi trong hội thoại Zalo. Việc nhận tin không ảnh hưởng đến đăng ký đã lưu.</p>
          <p className="muted">Mã yêu cầu: {receipt}</p>
          <Link to="/">Về trang chủ</Link>
          <button
            type="button"
            onClick={() => {
              setReceipt(undefined);
              setNotice('');
            }}
          >
            Gửi đăng ký khác
          </button>
        </section>
      </main>
    );
  return (
    <main className="consultation">
      <Link to="/utilities" className="back-link">
        ← Tiện ích
      </Link>
      <div className="page-heading registration-heading">
        <p className="eyebrow">TUYỂN SINH 2027</p>
        <h1>{REGISTRATION_TITLE}</h1>
        <p>Để nhà trường hiểu bạn hơn và hỗ trợ đúng thông tin bạn quan tâm.</p>
      </div>
      <div className="registration-outline" aria-label="Nội dung biểu mẫu">
        <span>1. Cá nhân</span><span>2. Học tập</span><span>3. Xác nhận</span>
      </div>
      <form onSubmit={(event) => void submit(event)} className="consultation-form registration-form">
        {invitation && <label><input type="checkbox" required checked={confirmZaloLink} onChange={e=>setConfirmZaloLink(e.target.checked)}/> Tôi xác nhận gửi thông tin vào hồ sơ được mời và liên kết tài khoản Zalo hiện tại khi phù hợp.</label>}
        <p className="field-help">Dấu * là thông tin bắt buộc. Số CCCD và email không bắt buộc. Đăng ký tìm hiểu không thay thế hồ sơ xét tuyển.</p>
        <fieldset disabled={busy}>
          <section className="registration-section" aria-labelledby="personal-heading">
            <h2 id="personal-heading">Thông tin cá nhân</h2>
            <p className="field-help">{native ? 'Chọn lấy tên hoặc số điện thoại từ Zalo nếu bạn muốn chia sẻ. Bạn vẫn có thể nhập trực tiếp.' : 'Bạn có thể sử dụng gợi ý thông tin từ trình duyệt.'}</p>
            <label htmlFor="consult-name">Họ tên học sinh *</label>
            <div className="autofill-field">
              <input id="consult-name" name="fullname" autoComplete="name" required minLength={2} maxLength={150} value={fields.fullname} onChange={e => set('fullname', e.target.value)} />
              {native && <button type="button" disabled={autofill} aria-busy={nameBusy} onClick={() => void fillName()}>Lấy tên Zalo</button>}
            </div>
            {nameBusy && <p className="field-help" role="status">Đang chờ phản hồi từ Zalo. Bạn vẫn có thể nhập họ tên trực tiếp.</p>}
            {nameNotice && <p className="form-notice name-notice" role="status">{nameNotice}</p>}
            {native && namePermission && <button className="permission-button" type="button" onClick={() => void openPermissions('name')}>Mở cài đặt quyền Zalo</button>}
            <label htmlFor="consult-national-id">Số CCCD <span className="optional-label">Không bắt buộc</span></label>
            <input id="consult-national-id" name="nationalId" type="text" inputMode="numeric" autoComplete="off" pattern="[0-9]{12}" maxLength={12} aria-describedby="national-id-help" value={fields.nationalId} onChange={e => set('nationalId', e.target.value)} />
            <p id="national-id-help" className="field-help">Nếu cung cấp, nhập đủ 12 chữ số. Không cần gửi ảnh giấy tờ.</p>
            <label htmlFor="consult-phone">Số điện thoại cá nhân *</label>
            <div className="autofill-field">
              <input id="consult-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" required maxLength={25} placeholder="xxxxxx" aria-describedby="consult-phone-help" onBlur={() => set('phone', formatPhoneForInput(fields.phone))} value={fields.phone} onChange={e => set('phone', e.target.value)} />
              {native && <button type="button" disabled={autofill} aria-busy={phoneBusy} onClick={() => void fillPhone()}>Lấy số Zalo</button>}
            </div>
            <p id="consult-phone-help" className="field-help">Chấp nhận đầu số 0, 84 hoặc +84. Số Việt Nam được hiển thị theo dạng trong nước.</p>
            {phoneBusy && <p className="field-help" role="status">Đang xin quyền và xác minh số điện thoại từ Zalo. Bạn vẫn có thể nhập trực tiếp.</p>}
            {phoneNotice && <p className="form-notice name-notice" role="status">{phoneNotice}</p>}
            {native && phonePermission && <button className="permission-button" type="button" onClick={() => void openPermissions('phone')}>Mở cài đặt quyền Zalo</button>}
            <label htmlFor="consult-email">Email cá nhân <span className="optional-label">Không bắt buộc</span></label>
            <input id="consult-email" name="email" type="email" autoComplete="email" maxLength={254} value={fields.email} onChange={e => set('email', e.target.value)} aria-describedby="email-help" />
            <p id="email-help" className="field-help">Zalo không cung cấp email. Bạn có thể nhập trực tiếp hoặc để trống.</p>
          </section>
          <section className="registration-section" aria-labelledby="education-heading">
            <h2 id="education-heading">Thông tin học tập</h2>
            <label htmlFor="consult-province">Trường THPT thuộc tỉnh/thành phố *</label>
            <select id="consult-province" name="schoolProvince" required value={fields.schoolProvince} onChange={e => set('schoolProvince', e.target.value)}>
              <option value="" disabled>Chọn tỉnh/thành phố</option>
              {SCHOOL_PROVINCES.map(province => <option key={province}>{province}</option>)}
            </select>
            <label htmlFor="consult-school">Tên trường THPT đang theo học *</label>
            <input id="consult-school" name="school" autoComplete="organization" required minLength={2} maxLength={200} placeholder="Ví dụ: Trường THPT Nguyễn Thị Minh Khai" value={fields.school} onChange={e => set('school', e.target.value)} />
            <fieldset className="registration-choices" aria-describedby="majors-help">
              <legend>Ngành học quan tâm *</legend>
              <p id="majors-help" className="field-help">Bạn có thể chọn nhiều ngành.</p>
              {INTERESTED_MAJORS.map((major, index) => <label className="choice-row" key={major}>
                <input id={index === 0 ? 'consult-major' : undefined} type="checkbox" required={index === 0 && fields.interestedMajors.length === 0} checked={fields.interestedMajors.includes(major)} onChange={e => set('interestedMajors', e.target.checked ? [...fields.interestedMajors, major] : fields.interestedMajors.filter(m => m !== major))} />
                <span>{major}</span>
              </label>)}
            </fieldset>
            <fieldset className="registration-choices">
              <legend>Khối lớp (năm học 2026–2027) *</legend>
              <div className="grade-choices">{SCHOOL_GRADES.map(grade => <label className="choice-row" key={grade}>
                <input type="radio" name="grade" required checked={fields.grade === grade} onChange={() => {set('grade', grade);set('gradeOther', '');}} /><span>{grade}</span>
              </label>)}</div>
              {fields.grade === 'Khác' && <><label htmlFor="consult-grade-other">Khối lớp hoặc tình trạng học tập khác *</label><input id="consult-grade-other" required maxLength={100} value={fields.gradeOther} onChange={e => set('gradeOther', e.target.value)} placeholder="Ví dụ: Đã tốt nghiệp THPT" /></>}
            </fieldset>
          </section>
          <section className="registration-section" aria-labelledby="confirmation-heading">
            <h2 id="confirmation-heading">Xác nhận đồng ý và cam kết</h2>
            <label className="consent"><input type="checkbox" required checked={fields.accuracyConfirmed} onChange={e => set('accuracyConfirmed', e.target.checked)} /><span>{REGISTRATION_ACCURACY}</span></label>
            <p className="muted">Khi đủ điều kiện gửi tin, bản xác nhận toàn bộ thông tin bạn cung cấp (kể cả số CCCD nếu có) sẽ được gửi vào đúng hội thoại Zalo OA đã liên kết. Tin đã gửi được lưu theo chính sách Zalo.</p>
            <label className="consent"><input type="checkbox" required checked={fields.consent} onChange={e => set('consent', e.target.checked)} /><span>{REGISTRATION_CONSENT}</span></label>
            <p className="field-help">Xem <ContentLink external url={privacyUrl()}>Điều khoản và chính sách dữ liệu cá nhân</ContentLink>. Thông tin chỉ được gửi sau khi bạn xác nhận và chọn gửi đăng ký.</p>
          </section>
        </fieldset>
        {notice && (
          <p className="form-notice" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button className="consultation-submit" type="submit" disabled={busy || autofill}>
          {busy ? 'Đang gửi…' : 'Gửi đăng ký tìm hiểu'}
        </button>
      </form>
    </main>
  );
}
